import { getPathname } from "@/i18n/navigation";

type RatingLinkParams = { orderId: string; configurationId: string };

/**
 * A rating deep link arrives as `?redirect_url=<orders page + modal params>`.
 * Only the two ids plus the `rateDiet` flag are carried over, everything else
 * from the original query is dropped on purpose.
 */
export const getRatingLinkParams = (
  redirectUrl: string | null,
): RatingLinkParams | null => {
  const queryIndex = redirectUrl?.indexOf("?") ?? -1;
  if (!redirectUrl || queryIndex === -1) {
    return null;
  }

  const query = new URLSearchParams(redirectUrl.slice(queryIndex + 1));
  if (query.get("rateDiet") !== "true") {
    return null;
  }

  const orderId = query.get("orderId");
  const configurationId = query.get("configurationId");
  if (!(orderId && configurationId)) {
    return null;
  }

  return { orderId, configurationId };
};

/**
 * Where a sign-in started from the dedicated page lands. A rating deep link
 * returns to the orders page with its modal params; every other login goes to
 * the default panel. Both carry `event=login` for analytics.
 */
export const getSignInRedirectUrl = ({
  locale,
  redirectUrlParam,
}: {
  locale: string;
  redirectUrlParam: string | null;
}) => {
  const ratingParams = getRatingLinkParams(redirectUrlParam);

  if (ratingParams) {
    return getPathname({
      href: {
        pathname: "/panel/orders",
        query: { ...ratingParams, rateDiet: "true", event: "login" },
      },
      locale,
    });
  }

  return getPathname({
    href: {
      pathname: "/panel/diets",
      query: { event: "login" },
    },
    locale,
  });
};

/** Sign-up keeps its own analytics event, but lands in the same place. */
export const getSignUpRedirectUrl = ({ locale }: { locale: string }) =>
  getPathname({
    href: {
      pathname: "/panel/diets",
      query: { event: "sign_up" },
    },
    locale,
  });

/**
 * Clerk expects auth paths without a trailing slash, which `getPathname` adds
 * for catch-all routes with no segments.
 */
export const withoutTrailingSlash = (pathname: string) =>
  pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

export const getSignInPathname = ({
  locale,
  preserveLocalePrefix = true,
}: {
  locale: string;
  preserveLocalePrefix?: boolean;
}) =>
  withoutTrailingSlash(
    getPathname({
      href: {
        pathname: "/sign-in/[[...sign-in]]",
        params: { "sign-in": [] },
      },
      locale,
      preserveLocalePrefix,
    }),
  );

export const getSignUpPathname = ({
  locale,
  preserveLocalePrefix = true,
}: {
  locale: string;
  preserveLocalePrefix?: boolean;
}) =>
  withoutTrailingSlash(
    getPathname({
      href: {
        pathname: "/sign-up/[[...sign-up]]",
        params: { "sign-up": [] },
      },
      locale,
      preserveLocalePrefix,
    }),
  );
